import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useCallback } from 'react';
import { selectEssentialWorkflowStatus } from 'routes/essential-workspace/state/form.selectors';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { selectEssentialFormInfos } from 'state/essential/infos/selectors';
import { isEssentialFormLoaded } from 'state/essential/selector';
import { essentialWorkspaceTabClonedThunk } from 'state/essential/thunks';
import { refreshScheduleThunk, resetDefaultDatesThunk } from 'state/formData/formData.thunks';
import { isPendingEnrichment } from 'state/selectors';
import { getTabIsFrozen, hasWorkspaceTabError } from 'state/workspaces/tabs/tabs.selector';
import {
  workspaceResetTabRequestedThunk,
  workspaceTabRemovedThunk,
  workspaceTabsAllRemovedThunk,
} from 'state/workspaces/workspace.thunks';

export function useCloseTab() {
  const dispatch = useThunkDispatch();
  return useCallback(
    (tabId: string) => {
      dispatch(workspaceTabRemovedThunk(tabId, 'essential', true));
      logAnalytics.trackEvent('Primary market essential', 'Close tab');
    },
    [dispatch],
  );
}
export function useCloseCurrentTab() {
  const closeTab = useCloseTab();
  const activeTabId = useAssertedActiveTabId();
  return useCallback(() => {
    closeTab(activeTabId);
  }, [closeTab, activeTabId]);
}

export function useClearAll() {
  const dispatch = useThunkDispatch();
  return () => {
    dispatch(workspaceTabsAllRemovedThunk('essential'));
    logAnalytics.trackEvent('Primary market essential', 'Clear all tabs confirmed');
  };
}

export function useResetTab() {
  const dispatch = useThunkDispatch();
  const tabId = useAssertedActiveTabId();

  const hasWorkspaceFatalError = useAppSelector(state => hasWorkspaceTabError(state, tabId));
  const isFormLoading = !useAppSelector(state => isEssentialFormLoaded(state, tabId));
  const isFormEnrichmentPending = useAppSelector(state => isPendingEnrichment(state, tabId));
  const quoteStatus = useAppSelector(state => selectEssentialWorkflowStatus(state, tabId));
  const isQuoting = quoteStatus === 'price-in-progress';
  const isTabFrozen = !!useAppSelector(state => getTabIsFrozen(state, tabId));
  const formInfos = useAppSelector(state => selectEssentialFormInfos(state, tabId));
  const isCrossBrowserDirty = !!formInfos?.crossBrowserDirty;

  const canReset =
    tabId !== undefined &&
    !hasWorkspaceFatalError &&
    !isFormLoading &&
    !isFormEnrichmentPending &&
    !isQuoting &&
    !isTabFrozen &&
    !isCrossBrowserDirty;

  const onResetTabClicked = useCallback(() => {
    dispatch(workspaceResetTabRequestedThunk(tabId, 'essential'));
  }, [dispatch, tabId]);

  const onRefreshScheduleClicked = useCallback(() => {
    dispatch(refreshScheduleThunk(tabId, tabId));
  }, [dispatch, tabId]);

  const onRefreshDefaultDatesClicked = useCallback(() => {
    dispatch(resetDefaultDatesThunk(tabId, tabId));
  }, [dispatch, tabId]);

  return { canReset, onResetTabClicked, onRefreshScheduleClicked, onRefreshDefaultDatesClicked };
}

export function useCloneTab() {
  const dispatch = useThunkDispatch();
  const tabId = useAssertedActiveTabId();

  const hasWorkspaceFatalError = useAppSelector(state => hasWorkspaceTabError(state, tabId));
  const isFormLoading = !useAppSelector(state => isEssentialFormLoaded(state, tabId));
  const isFormEnrichmentPending = useAppSelector(state => isPendingEnrichment(state, tabId));
  const formInfos = useAppSelector(state => selectEssentialFormInfos(state, tabId));
  const isCrossBrowserDirty = !!formInfos?.crossBrowserDirty;

  const canClone = !hasWorkspaceFatalError && !isFormLoading && !isFormEnrichmentPending && !isCrossBrowserDirty;

  const onCloneTabClicked = () => {
    dispatch(essentialWorkspaceTabClonedThunk(tabId, 'essential', 'quote'));
    logAnalytics.trackEvent('Primary market essential', 'Clone tab');
  };

  return { canClone, onCloneTabClicked };
}
